// PWP Assistant config (GET / PUT /admin/assistant/config): wire types and response normalisers.
// Shared by the RTK Query slice and the config page, and import-free so `unit/` can run it in plain Node.

export type PlanKey = 'free' | 'pro' | 'proplus'
export const PLAN_KEYS: PlanKey[] = ['free', 'pro', 'proplus']

export interface AssistantCaps {
  memberDailyMessages: number
  companyMonthlyCostCents: number
}

export interface AssistantCompanyOverride {
  companyId: string
  memberDailyMessages?: number
  companyMonthlyCostCents?: number
}

export type ModelRole = 'router' | 'answer'
export const MODEL_ROLES: ModelRole[] = ['router', 'answer']

export interface AssistantModelSetting {
  model: string
  inputPerMTokCents: number
  outputPerMTokCents: number
}

/** `null` = that model is not set yet. */
export type AssistantModels = Record<ModelRole, AssistantModelSetting | null>

export interface AssistantConfigPayload {
  enabled: boolean
  companyAllowlist: string[]
  capsByPlan: Record<PlanKey, AssistantCaps>
  companyOverrides: AssistantCompanyOverride[]
  /** Absent from a response = a backend without models. Absent from a PUT = the server keeps the stored ones. */
  models?: AssistantModels
  /**
   * Spend ceiling per UTC day across every company, in integer cents (0 = no replies). Absent from a response = a
   * backend without it. Absent from a PUT = the server keeps the stored value.
   */
  platformDailyCostCents?: number
}

/** PUT body. `revision` is the one that was loaded; it is left out only when the server never sent one. */
export interface AssistantConfigSave extends AssistantConfigPayload {
  revision?: number
}

export interface AssistantConfigEditor {
  id: string
  name: string
  email: string
}

/** Read-only, never sent. `missing` codes: apiKey, routerModel, answerModel, knowledgePack (others shown raw). */
export interface AssistantAvailability {
  ok: boolean
  missing: string[]
}

export interface AssistantConfig extends AssistantConfigPayload {
  /** `null` = the server did not send one (a backend from before the save guard). */
  revision: number | null
  updatedAt: string | null
  updatedBy: AssistantConfigEditor | null
  /** Absent = a backend without it, or a body this page cannot read: nothing is shown. */
  availability?: AssistantAvailability
  /**
   * Read-only, never sent: today's (UTC) spend across every company, in cents - fractional (the server keeps
   * fractions of a cent). Absent or unreadable = not shown.
   */
  platformSpendTodayCents?: number
}

export interface CompanySummary {
  _id: string
  companyName: string
  planType: string
}

export const MALFORMED_ASSISTANT_CONFIG = 'Malformed assistant config response'

type Dict = Record<string, unknown>
const isDict = (value: unknown): value is Dict => typeof value === 'object' && value !== null && !Array.isArray(value)
const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)
const isCents = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0
const asString = (value: unknown): string => (typeof value === 'string' ? value : '')
const malformed = (): never => {
  throw new Error(MALFORMED_ASSISTANT_CONFIG)
}

const normalizeCaps = (raw: unknown): AssistantCaps => {
  if (!isDict(raw) || !isFiniteNumber(raw.memberDailyMessages) || !isFiniteNumber(raw.companyMonthlyCostCents))
    return malformed()
  return { memberDailyMessages: raw.memberDailyMessages, companyMonthlyCostCents: raw.companyMonthlyCostCents }
}

const normalizeOverride = (raw: unknown): AssistantCompanyOverride => {
  if (!isDict(raw) || typeof raw.companyId !== 'string') return malformed()
  const cap = (value: unknown): number | undefined => {
    if (value === undefined || value === null) return undefined
    return isFiniteNumber(value) ? value : malformed()
  }
  return {
    companyId: raw.companyId,
    memberDailyMessages: cap(raw.memberDailyMessages),
    companyMonthlyCostCents: cap(raw.companyMonthlyCostCents),
  }
}

// Current shape is `{ id, name, email }`; the first backend release sent the bare admin id.
const normalizeEditor = (raw: unknown): AssistantConfigEditor | null => {
  if (typeof raw === 'string') return raw ? { id: raw, name: '', email: '' } : null
  if (!isDict(raw)) return null
  return { id: asString(raw.id) || asString(raw._id), name: asString(raw.name), email: asString(raw.email) }
}

// Absent is tolerated (unknown revision), so a backend that has not shipped it yet still loads. Present but
// not a non-negative integer is malformed: a guessed revision must never be sent back.
const normalizeRevision = (raw: unknown): number | null => {
  if (raw === undefined) return null
  return typeof raw === 'number' && Number.isInteger(raw) && raw >= 0 ? raw : malformed()
}

// Absent (or null) = a backend without the ceiling: the field is hidden and never sent. Present, it is sent back on
// the next save, so anything but integer cents >= 0 is malformed.
const normalizeCeiling = (raw: unknown): number | undefined => {
  if (raw === undefined || raw === null) return undefined
  return isCents(raw) ? raw : malformed()
}

// Absent = a backend without models. Present, each role is a full setting or null (not set). Anything else is
// malformed: these values are sent back on the next save.
const normalizeModels = (raw: unknown): AssistantModels | undefined => {
  if (raw === undefined) return undefined
  if (!isDict(raw)) return malformed()
  const setting = (value: unknown): AssistantModelSetting | null => {
    if (value === undefined || value === null) return null
    if (
      !isDict(value) ||
      typeof value.model !== 'string' ||
      !isFiniteNumber(value.inputPerMTokCents) ||
      !isFiniteNumber(value.outputPerMTokCents)
    )
      return malformed()
    return {
      model: value.model,
      inputPerMTokCents: value.inputPerMTokCents,
      outputPerMTokCents: value.outputPerMTokCents,
    }
  }
  return { router: setting(raw.router), answer: setting(raw.answer) }
}

// Informational only and never sent back, so lenient like the audit fields: an unreadable one is not shown.
const normalizeAvailability = (raw: unknown): AssistantAvailability | undefined => {
  if (!isDict(raw) || typeof raw.ok !== 'boolean' || !Array.isArray(raw.missing)) return undefined
  const missing = raw.missing as unknown[]
  return missing.every((code) => typeof code === 'string') ? { ok: raw.ok, missing: missing as string[] } : undefined
}

/**
 * Throws on anything that is not a complete config. A kill switch must never be drawn from a filled-in
 * default: a body this cannot read becomes a failed load (or a failed save), not an "off, no caps" form
 * that the next full-replace PUT would write back. Only the audit fields, `availability` and
 * `platformSpendTodayCents` are lenient.
 */
export const normalizeAssistantConfig = (raw: unknown): AssistantConfig => {
  if (!isDict(raw) || typeof raw.enabled !== 'boolean' || !isDict(raw.capsByPlan)) return malformed()
  if (!Array.isArray(raw.companyAllowlist) || !Array.isArray(raw.companyOverrides)) return malformed()
  const allowlist = raw.companyAllowlist as unknown[]
  if (!allowlist.every((id) => typeof id === 'string')) return malformed()
  const models = normalizeModels(raw.models)
  const availability = normalizeAvailability(raw.availability)
  const platformDailyCostCents = normalizeCeiling(raw.platformDailyCostCents)
  return {
    enabled: raw.enabled,
    companyAllowlist: allowlist as string[],
    capsByPlan: {
      free: normalizeCaps(raw.capsByPlan.free),
      pro: normalizeCaps(raw.capsByPlan.pro),
      proplus: normalizeCaps(raw.capsByPlan.proplus),
    },
    companyOverrides: (raw.companyOverrides as unknown[]).map(normalizeOverride),
    ...(models && { models }),
    ...(platformDailyCostCents !== undefined && { platformDailyCostCents }),
    revision: normalizeRevision(raw.revision),
    updatedAt: typeof raw.updatedAt === 'string' && raw.updatedAt ? raw.updatedAt : null,
    updatedBy: normalizeEditor(raw.updatedBy),
    ...(availability && { availability }),
    // Informational only, like `availability`: an unreadable one is not shown. A sum of fractional cents.
    ...(isFiniteNumber(raw.platformSpendTodayCents) &&
      raw.platformSpendTodayCents >= 0 && { platformSpendTodayCents: raw.platformSpendTodayCents }),
  }
}

export const toCompanySummary = (raw: unknown): CompanySummary | null => {
  if (!isDict(raw) || !asString(raw._id)) return null
  return { _id: asString(raw._id), companyName: asString(raw.companyName), planType: asString(raw.planType) }
}
